import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useSession, useI18n, PullIndicator, usePullToRefresh } from 'gunsi-auth'
import { useEven } from '../hooks/useEven.jsx'
import { useEvenI18n } from '../i18n/even.js'
import {
  computeBalancesByCurrency, computeBalancesConverted, pairwiseDebts,
  youOweFromDebts, youSummaryByCurrency, usedCurrencies, expensePayers, itemCurrency,
  memberShareOf, paidAmountOf,
} from '../util/balances.js'
import { getRatesForDates, convert } from '../util/fx.js'
import { youOf, makeMemberLabel } from '../util/you.js'
import { fmtMoney, fmtDate } from '../format.js'
import { Select } from '../components/Select.jsx'
import { ExpenseFormSheet } from './ExpenseForm.jsx'
import { ExpenseDetailsSheet } from './ExpenseDetails.jsx'
import { SettleFormSheet } from './SettleForm.jsx'
import { GroupFormSheet } from './GroupForm.jsx'

const CATEGORY_EMOJI = {
  food: '🍕', drinks: '🍻', groceries: '🛒', transport: '🚆', lodging: '🏨',
  entertainment: '🎬', utilities: '💡', shopping: '🛍️', other: '💸',
}

// Preferenza per-gruppo della vista valute (null = separate), persistita in LS
const CURVIEW_LS = 'gunsi_even_curview_v1'
function loadCurView(groupId) {
  try { return JSON.parse(localStorage.getItem(CURVIEW_LS))?.[groupId] ?? null } catch { return null }
}
function saveCurView(groupId, cur) {
  try {
    const map = JSON.parse(localStorage.getItem(CURVIEW_LS)) ?? {}
    if (cur) map[groupId] = cur
    else delete map[groupId]
    localStorage.setItem(CURVIEW_LS, JSON.stringify(map))
  } catch {}
}

export function GroupDetail() {
  const t = useEvenI18n()
  const { id } = useParams()
  const navigate = useNavigate()
  const { session } = useSession()
  const { locale } = useI18n()
  const { groups, expenses, settlements, loading, currency: userCurrency, refresh } = useEven()
  const { listRef, pull, refreshing, ready } = usePullToRefresh(refresh)
  const group = groups.find(g => g.id === id)
  const [tab, setTab] = useState('expenses')
  const [showExp, setShowExp] = useState(null) // null | true | expenseObj (form)
  const [showDetail, setShowDetail] = useState(null) // expenseObj (dettagli)
  const [showSettle, setShowSettle] = useState(null)
  const [editGroup, setEditGroup] = useState(false)
  // Vista bilanci: null = separata per valuta; codice = tutto convertito lì
  const [viewCur, setViewCur] = useState(() => loadCurView(id))
  // Il componente resta montato navigando tra gruppi: ricarica la preferenza
  useEffect(() => { setViewCur(loadCurView(id)) }, [id])
  function changeViewCur(cur) {
    setViewCur(cur)
    saveCurView(id, cur)
  }
  const [ratesByDate, setRatesByDate] = useState(null)
  const [ratesLoading, setRatesLoading] = useState(false)
  // Due viste distinte sui debiti, non un'opzione nascosta: "Bilanci" dice
  // chi deve a chi davvero, "Chiusura" il piano più corto per azzerare il
  // gruppo — che però reindirizza i debiti verso terzi e vale solo se
  // pareggiano tutti insieme.
  const simplify = tab === 'settleplan'

  const gExp = useMemo(
    () => expenses.filter(e => e.groupId === id)
                  .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? '')),
    [expenses, id],
  )
  const gSet = useMemo(
    () => settlements.filter(s => s.groupId === id)
                     .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? '')),
    [settlements, id],
  )

  const usedCurs = useMemo(
    () => group ? usedCurrencies(group, gExp, gSet) : [],
    [group, gExp, gSet],
  )
  const multiCur = usedCurs.length > 1
  // Target della vista unificata: la default currency dell'utente sta in
  // cima ed è sempre offerta (utile anche con gruppo mono-valuta estera)
  const viewCurs = useMemo(
    () => [...new Set([userCurrency, ...usedCurs])].filter(Boolean),
    [userCurrency, usedCurs],
  )
  const canConvert = multiCur ||
    (usedCurs.length === 1 && Boolean(userCurrency) && usedCurs[0] !== userCurrency)

  // Tassi storici (una fetch per data, cache immutabile) quando serve la
  // vista unificata
  useEffect(() => {
    if (viewCur === null || !canConvert) return
    let cancelled = false
    setRatesLoading(true)
    const dates = [...gExp, ...gSet].map(i => (i.date || '').slice(0, 10)).filter(Boolean)
    getRatesForDates(dates).then(map => {
      if (cancelled) return
      setRatesByDate(map)
      setRatesLoading(false)
    })
    return () => { cancelled = true }
  }, [viewCur, canConvert, gExp, gSet])

  if (loading && !group) {
    return <div className="loading-screen"><div className="loading-spinner" /></div>
  }
  if (!group) return <Navigate to="/groups" replace />

  const you = youOf(group, session?.user?.id)
  const memberLabel = makeMemberLabel(group)

  const unified = viewCur !== null && canConvert

  // Fattore di conversione della voce verso la valuta della vista unificata
  // (tasso storico della sua data). null = nessuna conversione da fare.
  function convFactorOf(item) {
    if (!unified || !ratesByDate) return null
    const from = itemCurrency(item, group)
    if (from === viewCur) return null
    const conv = convert(item.amount, from, viewCur, ratesByDate[(item.date || '').slice(0, 10)])
    if (conv == null || !item.amount) return null
    return conv / item.amount
  }
  const converted = unified && !ratesLoading && ratesByDate
    ? computeBalancesConverted({ group, expenses: gExp, settlements: gSet, target: viewCur, ratesByDate })
    : null

  // Riepilogo personale
  const sumRows = youSummaryByCurrency(group, gExp, gSet, you?.id)
    .filter(s => Math.abs(s.net) >= 0.005)
  const uniSummary = converted ? youOweFromDebts(converted.debts, you?.id) : null

  // Bilanci per il tab. Di default i debiti REALI fra coppie; col toggle, il
  // piano accorciato (che però reindirizza i debiti verso terzi).
  const byCur = !unified ? computeBalancesByCurrency({ group, expenses: gExp, settlements: gSet }) : null
  const debtSections = !unified
    ? (byCur?.currencies ?? [])
        .map(c => ({
          currency: c,
          debts: simplify
            ? byCur.ledgers[c].debts
            : pairwiseDebts({
                group,
                expenses: gExp.filter(e => itemCurrency(e, group) === c),
                settlements: gSet.filter(s => itemCurrency(s, group) === c),
                amountOf: it => it.amount,
              }).debts,
        }))
        .filter(s => s.debts.length > 0)
    : null
  // Vista unificata: stessa scelta, sui movimenti convertiti al tasso storico
  const convertedDebts = !converted ? null : simplify
    ? converted.debts
    : pairwiseDebts({
        group, expenses: gExp, settlements: gSet,
        amountOf: (it) => {
          const from = itemCurrency(it, group)
          if (from === viewCur) return it.amount
          return convert(it.amount, from, viewCur, ratesByDate?.[(it.date || '').slice(0, 10)])
        },
      }).debts

  const bannerKind = (() => {
    if (unified) {
      if (!uniSummary || Math.abs(uniSummary.net) < 0.005) return 'even'
      return uniSummary.net > 0 ? 'owed' : 'owe'
    }
    if (sumRows.length === 0) return 'even'
    if (sumRows.every(s => s.net > 0)) return 'owed'
    if (sumRows.every(s => s.net < 0)) return 'owe'
    return 'mixed'
  })()

  return (
    <main className="page page-group">
      <header className="group-head">
        <button className="btn btn-ghost btn-back" onClick={() => navigate('/groups')}>‹ {t.common.back}</button>
        <div className="group-head-title">
          <div className="group-emoji-lg">{group.emoji || '👥'}</div>
          <div>
            <h1>{group.name}</h1>
            <div className="group-head-sub">
              {(group.members ?? []).map(m => memberLabel(m.id)).join(', ')}
            </div>
          </div>
        </div>
        <button type="button" className="group-edit-btn" onClick={() => setEditGroup(true)} aria-label={t.group.edit} title={t.group.edit}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
          </svg>
        </button>
      </header>

      {group.archivedAt && (
        <div className="archived-banner">{t.group.archived}</div>
      )}

      <section className={`you-summary you-${bannerKind}`}>
        <div className="you-summary-body">
          {unified ? (
            ratesLoading || !converted ? (
              <div className="loading-spinner small" />
            ) : (
              <div className="you-line">
                {Math.abs(uniSummary.net) < 0.005 ? (
                  <strong>{t.groups.settled}</strong>
                ) : uniSummary.net > 0 ? (
                  <><strong>≈ {fmtMoney(uniSummary.net, viewCur)}</strong> · {t.groups.youAreOwed}</>
                ) : (
                  <><strong>≈ {fmtMoney(-uniSummary.net, viewCur)}</strong> · {t.groups.youOwe}</>
                )}
              </div>
            )
          ) : sumRows.length === 0 ? (
            <strong>{t.groups.settled}</strong>
          ) : (
            sumRows.map(s => (
              <div key={s.currency} className={`you-line line-${s.net > 0 ? 'owed' : 'owe'}`}>
                {s.net > 0 ? (
                  <><strong>{fmtMoney(s.net, s.currency)}</strong> · {t.groups.youAreOwed}</>
                ) : (
                  <><strong>{fmtMoney(-s.net, s.currency)}</strong> · {t.groups.youOwe}</>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {canConvert && (
        <div className="cur-view-row">
          <Select
            value={viewCur ?? ''}
            onChange={v => changeViewCur(v === '' ? null : v)}
            options={[
              { value: '', label: t.group.viewSeparate },
              ...viewCurs.map(c => ({ value: c, label: t.group.viewAllIn.replace('{cur}', c) })),
            ]}
            label={t.group.currencyView}
          />
        </div>
      )}

      <div className="action-row">
        <button className="btn btn-primary" onClick={() => setShowExp(true)}>+ {t.group.addExpense}</button>
        <button className="btn btn-secondary" onClick={() => setShowSettle(true)}>≈ {t.group.addPayment}</button>
      </div>

      <nav className="subtabs">
        <button className={tab === 'expenses' ? 'active' : ''} onClick={() => setTab('expenses')}>{t.group.expenses}</button>
        <button className={tab === 'balances' ? 'active' : ''} onClick={() => setTab('balances')}>{t.group.balances}</button>
        <button className={tab === 'settleplan' ? 'active' : ''} onClick={() => setTab('settleplan')}>{t.group.settlePlan}</button>
      </nav>

      {/* Pull-to-refresh: trascinando giù dalla cima della lista si ricarica */}
      <div className="ptr-wrap">
        <PullIndicator pull={pull} refreshing={refreshing} ready={ready} />

      {tab === 'expenses' && (
        <section
          className="exp-list"
          ref={listRef}
          style={{ transform: pull ? `translateY(${pull}px)` : undefined, transition: pull ? undefined : 'transform 180ms ease-out' }}
        >
          {gExp.length === 0 && gSet.length === 0 ? (
            <div className="empty-state"><p>{t.group.noExpenses}</p></div>
          ) : (
            mergeAndSort(gExp, gSet).map(item => {
              const own = itemCurrency(item, group)
              const factor = convFactorOf(item)   // null = nessuna conversione
              if (item._type === 'expense') {
                const e = item
                const pl = expensePayers(e)
                const isYouPayer = Boolean(you && pl.some(p => p.memberId === you.id))
                const payerText = pl.length > 1
                  ? t.group.paidByN.replace('{n}', String(pl.length))
                  : (isYouPayer ? t.group.youPaid : `${memberLabel(pl[0]?.memberId)} ${t.group.paid}`)
                // Netto personale sulla spesa: anticipato − quota
                const net = you
                  ? Math.round((paidAmountOf(e, you.id) - memberShareOf(e, you.id)) * 100) / 100
                  : 0
                const involved = Boolean(you) && (
                  paidAmountOf(e, you.id) > 0.004 || memberShareOf(e, you.id) > 0.004
                )
                const kind = !involved ? 'none' : net > 0.004 ? 'lent' : net < -0.004 ? 'borrowed' : 'even'
                const label = kind === 'lent' ? t.activity.youLent
                  : kind === 'borrowed' ? t.group.youBorrowed
                  : kind === 'even' ? t.group.evenShare
                  : t.group.notInvolved
                const abs = Math.abs(net)
                return (
                  <div key={`e-${e.id}`} className="exp-row" onClick={() => setShowDetail(e)}>
                    <DateCol iso={e.date} locale={locale} />
                    <div className="exp-emoji">{e.emoji || CATEGORY_EMOJI[e.category] || '💸'}</div>
                    <div className="exp-meta">
                      <div className="exp-title">{e.description || t.activity.expense}</div>
                      <div className="exp-sub">{payerText} {fmtMoney(e.amount, own)}</div>
                    </div>
                    <div className="exp-amount-wrap">
                      <div className={`exp-impact-label ${kind}`}>{label}</div>
                      {kind !== 'none' && kind !== 'even' && (
                        <>
                          <div className={`exp-amount ${kind === 'lent' ? 'paid-you' : 'paid-other'}`}>
                            {factor != null ? `≈ ${fmtMoney(abs * factor, viewCur)}` : fmtMoney(abs, own)}
                          </div>
                          {factor != null && (
                            <div className="exp-amount-orig">{fmtMoney(abs, own)}</div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )
              } else {
                const s = item
                const kind = you && s.to === you.id ? 'lent' : you && s.from === you.id ? 'borrowed' : 'none'
                const label = kind === 'lent' ? t.activity.youReceived
                  : kind === 'borrowed' ? t.activity.youSettled
                  : t.activity.payment
                return (
                  <div key={`s-${s.id}`} className="exp-row settle-row" onClick={() => setShowSettle(s)}>
                    <DateCol iso={s.date} locale={locale} />
                    <div className="exp-emoji">≈</div>
                    <div className="exp-meta">
                      <div className="exp-title">{memberLabel(s.from)} → {memberLabel(s.to)}</div>
                      <div className="exp-sub">{t.activity.payment}</div>
                    </div>
                    <div className="exp-amount-wrap">
                      <div className="exp-impact-label">{label}</div>
                      <div className="exp-amount muted">
                        {factor != null ? `≈ ${fmtMoney(s.amount * factor, viewCur)}` : fmtMoney(s.amount, own)}
                      </div>
                      {factor != null && (
                        <div className="exp-amount-orig">{fmtMoney(s.amount, own)}</div>
                      )}
                    </div>
                  </div>
                )
              }
            })
          )}
        </section>
      )}

      {(tab === 'balances' || tab === 'settleplan') && (
        <section
          className="balances"
          ref={listRef}
          style={{ transform: pull ? `translateY(${pull}px)` : undefined, transition: pull ? undefined : 'transform 180ms ease-out' }}
        >
          <div className="simplify-hint">{simplify ? t.group.simplifyOn : t.group.simplifyOff}</div>

          {unified ? (
            ratesLoading || !converted ? (
              <div className="loading-screen"><div className="loading-spinner" /></div>
            ) : convertedDebts.length === 0 ? (
              <div className="empty-state"><p>{t.group.noBalances}</p></div>
            ) : (
              <>
                <ul className="debt-list">
                  {convertedDebts.map((d, i) => (
                    <li key={i} className="debt-row">
                      <span className="debt-from">{memberLabel(d.from)}</span>
                      <span className="debt-arrow">→</span>
                      <span className="debt-to">{memberLabel(d.to)}</span>
                      <span className="debt-amount">≈ {fmtMoney(d.amount, viewCur)}</span>
                    </li>
                  ))}
                </ul>
                {converted.missing && <div className="form-hint warn">{t.group.ratesMissing}</div>}
              </>
            )
          ) : debtSections.length === 0 ? (
            <div className="empty-state"><p>{t.group.noBalances}</p></div>
          ) : (
            debtSections.map(sec => (
              <div key={sec.currency} className="debt-section">
                {multiCur && <div className="debt-cur-head">{sec.currency}</div>}
                <ul className="debt-list">
                  {sec.debts.map((d, i) => (
                    <li key={i} className="debt-row">
                      <span className="debt-from">{memberLabel(d.from)}</span>
                      <span className="debt-arrow">→</span>
                      <span className="debt-to">{memberLabel(d.to)}</span>
                      <span className="debt-amount">{fmtMoney(d.amount, sec.currency)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </section>
      )}
      </div>

      <ExpenseDetailsSheet
        open={Boolean(showDetail)}
        group={group}
        expense={showDetail}
        onClose={() => setShowDetail(null)}
        // Prima si chiude il dettaglio, POI si apre il form. Aprirli nello
        // stesso istante rompe su iOS: ogni sheet tiene una voce di
        // cronologia, e l'history.back() della chiusura arriva più tardi del
        // pushState dell'apertura, portandosi via il form appena aperto.
        onEdit={(e) => { setShowDetail(null); setTimeout(() => setShowExp(e), 240) }}
      />
      <ExpenseFormSheet
        open={Boolean(showExp)}
        group={group}
        expense={typeof showExp === 'object' ? showExp : null}
        onClose={() => setShowExp(null)}
      />
      <SettleFormSheet
        open={Boolean(showSettle)}
        group={group}
        settlement={typeof showSettle === 'object' ? showSettle : null}
        onClose={() => setShowSettle(null)}
      />
      <GroupFormSheet
        open={editGroup}
        group={group}
        onClose={() => setEditGroup(false)}
      />
    </main>
  )
}

// Colonna data stile Splitwise: mese abbreviato sopra, giorno sotto
function DateCol({ iso, locale }) {
  const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return <div className="exp-date" />
  return (
    <div className="exp-date">
      <span className="exp-date-m">
        {new Intl.DateTimeFormat(lang, { month: 'short' }).format(d).replace('.', '')}
      </span>
      <span className="exp-date-d">{d.getDate()}</span>
    </div>
  )
}

function mergeAndSort(expenses, settlements) {
  const a = expenses.map(e => ({ ...e, _type: 'expense' }))
  const b = settlements.map(s => ({ ...s, _type: 'settlement' }))
  return [...a, ...b].sort((x, y) => (y.date ?? '').localeCompare(x.date ?? ''))
}
